import { PNL_ACTUALS_SCENARIO_ID } from 'src/components/star-grid/starGridConstants';
import { PnlPivotCalcColumn, PnlPivotColumnGroup, PnlScenarioEntry, PnlVariancePair } from 'src/components/star-grid/starGridTypes';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const formatMonthShort = (month: number): string => `${MONTH_NAMES[(month % 100) - 1]} ${String(Math.floor(month / 100)).slice(-2)}`;
export const pivotFieldId = (scenario: number, month: number): string => `s${scenario}_m${month}`;
export const effectiveScenarioId = (scenario: PnlScenarioEntry, month: number): number =>
  scenario.forecast_start_period && month < scenario.forecast_start_period ? PNL_ACTUALS_SCENARIO_ID : scenario.reporting_scenario_id;

export const declaredPeriodGroups = (scenarios: readonly PnlScenarioEntry[]): PnlPivotColumnGroup[] =>
  scenarios.map((scenario, groupIndex) => ({
    group_label: scenario.label,
    columns: [...new Set(scenario.months)].map((month) => ({
      field: pivotFieldId(effectiveScenarioId(scenario, month), month),
      colId: `period_${groupIndex}_${month}`,
      periodType: effectiveScenarioId(scenario, month) === PNL_ACTUALS_SCENARIO_ID ? ('actual' as const) : ('forecast' as const),
      headerName: formatMonthShort(month)
    }))
  }));

export const additionalPeriodGroups = (observed: Map<number, Set<number>>, declared: readonly PnlPivotColumnGroup[]): PnlPivotColumnGroup[] => {
  const covered = new Set(declared.flatMap((group) => group.columns.map((column) => column.field)));
  return [...observed]
    .sort(([a], [b]) => a - b)
    .flatMap(([scenario, months]) => {
      const extra = [...months].sort((a, b) => a - b).filter((month) => !covered.has(pivotFieldId(scenario, month)));
      return extra.length
        ? [
            {
              group_label: `Scenario ${scenario}`,
              columns: extra.map((month) => ({
                field: pivotFieldId(scenario, month),
                headerName: formatMonthShort(month)
              }))
            }
          ]
        : [];
    });
};

const varianceColumn = (
  pair: PnlVariancePair,
  month: number,
  index: number,
  pairIndex: number,
  scenarios: readonly PnlScenarioEntry[]
): PnlPivotCalcColumn[] => {
  const compareMonth = pair.compare_months[index];
  const base = scenarios.find((scenario) => scenario.label === pair.base_scenario && scenario.months.includes(month));
  const compare = scenarios.find((scenario) => scenario.label === pair.compare_scenario && scenario.months.includes(compareMonth));
  if (!base || !compare) return [];
  return [
    {
      colId: `variance_${pairIndex}_${index}`,
      headerName: month === compareMonth ? formatMonthShort(month) : `${formatMonthShort(month)} vs ${formatMonthShort(compareMonth)}`,
      expression: `[${pivotFieldId(effectiveScenarioId(base, month), month)}] - [${pivotFieldId(
        effectiveScenarioId(compare, compareMonth),
        compareMonth
      )}]`,
      group: `${pair.base_scenario} vs ${pair.compare_scenario}`
    }
  ];
};

/** Plain monthly variances only. Ex-FX and deck periods require the backend column axis. */
export const monthlyVarianceColumns = (pairs: readonly PnlVariancePair[], scenarios: readonly PnlScenarioEntry[]): PnlPivotCalcColumn[] =>
  pairs.flatMap((pair, pairIndex) => pair.base_months.flatMap((month, index) => varianceColumn(pair, month, index, pairIndex, scenarios)));
